import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  Check,
  Copy,
  Crown,
  Database,
  Download,
  Eraser,
  Eye,
  Flame,
  Key,
  KeyRound,
  Loader2,
  LogOut,
  Mail,
  Pencil,
  Plus,
  ShieldCheck,
  Users,
  Trophy,
  Activity,
  TrendingUp,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SaasInquiriesPanel from "@/components/SaasInquiriesPanel";

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-[0_1px_0_rgba(10,37,64,0.04)]"
    >
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.18em] text-slate-500">
          {label}
        </span>
        <span
          className={`flex h-8 w-8 items-center justify-center rounded-md ${accent}`}
        >
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="font-display mt-3 text-3xl font-bold tracking-tight text-[#0A2540]">
        {value ?? "—"}
      </div>
    </div>
  );
}

function CreateTenantDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [plan, setPlan] = useState("starter");
  const [createAdmin, setCreateAdmin] = useState(true);
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [sendWelcome, setSendWelcome] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [credentials, setCredentials] = useState(null); // {email, name, temp_password, welcome_email_sent}
  const [copied, setCopied] = useState(false);

  const reset = () => {
    setName("");
    setSlug("");
    setPlan("starter");
    setCreateAdmin(true);
    setAdminName("");
    setAdminEmail("");
    setSendWelcome(true);
    setCredentials(null);
    setCopied(false);
  };

  const handleOpenChange = (next) => {
    setOpen(next);
    if (!next) {
      // Defer the reset so the closing animation doesn't flash blank fields
      setTimeout(reset, 200);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = {
        name: name.trim(),
        slug: slug.trim().toLowerCase(),
        plan,
      };
      if (createAdmin && adminEmail.trim()) {
        body.admin_email = adminEmail.trim();
        body.admin_name = adminName.trim() || adminEmail.split("@")[0];
        body.send_welcome_email = sendWelcome;
      }
      const r = await api.post("/super/tenants", body);
      toast.success(`Tenant "${name}" created.`);

      if (r.data.admin && r.data.admin.temp_password) {
        // Show one-time credentials reveal screen
        setCredentials(r.data.admin);
      } else {
        // No admin created — close immediately
        handleOpenChange(false);
      }
      onCreated?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const copyCredentials = async () => {
    if (!credentials) return;
    const text =
      `MedAdmits Admin · ${name}\n` +
      `Login URL: ${window.location.origin}/admin/login\n` +
      `Email: ${credentials.email}\n` +
      `Password: ${credentials.temp_password}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Credentials copied to clipboard");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Couldn't access clipboard — copy manually below.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
          data-testid="super-create-tenant-btn"
        >
          <Plus className="mr-1.5 h-4 w-4" /> New Tenant
        </Button>
      </DialogTrigger>
      <DialogContent
        data-testid="super-create-tenant-dialog"
        className="sm:max-w-lg"
      >
        {credentials ? (
          // ---- ONE-TIME CREDENTIALS REVEAL ----
          <div data-testid="tenant-credentials-reveal">
            <DialogHeader>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#C9A961]">
                <Key className="h-3.5 w-3.5" /> One-time credentials
              </div>
              <DialogTitle className="mt-2 text-2xl text-[#0A2540]">
                Tenant created. Save these now.
              </DialogTitle>
            </DialogHeader>
            <p className="mt-3 text-sm text-slate-600">
              This password is shown <strong>once</strong>. After you close this dialog
              it's hashed in our database and cannot be retrieved. {credentials.welcome_email_sent && (
                <span> A welcome email was also sent to <strong>{credentials.email}</strong>.</span>
              )}
            </p>

            <div className="mt-5 rounded-lg border border-[#C9A961]/40 bg-[#FAF9F6] p-4 space-y-3">
              <CredField
                label="Login URL"
                value={`${window.location.origin}/admin/login`}
                testid="creds-login-url"
              />
              <CredField label="Email" value={credentials.email} testid="creds-email" />
              <CredField
                label="Temporary password"
                value={credentials.temp_password}
                mono
                strong
                testid="creds-password"
              />
            </div>

            {credentials.welcome_email_error && (
              <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900">
                ⚠ Welcome email failed to send: {credentials.welcome_email_error}.
                Please share the credentials manually.
              </div>
            )}

            <DialogFooter className="mt-6 gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={copyCredentials}
                data-testid="creds-copy-all"
              >
                {copied ? (
                  <>
                    <Check className="mr-1.5 h-4 w-4" /> Copied
                  </>
                ) : (
                  <>
                    <Copy className="mr-1.5 h-4 w-4" /> Copy all
                  </>
                )}
              </Button>
              <Button
                type="button"
                className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
                onClick={() => handleOpenChange(false)}
                data-testid="creds-done"
              >
                I've saved them — done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          // ---- CREATION FORM ----
          <>
            <DialogHeader>
              <DialogTitle>Onboard a new consultancy</DialogTitle>
            </DialogHeader>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="tenant-name">Consultancy name</Label>
                <Input
                  id="tenant-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Sunrise MBBS Consultancy"
                  className="mt-1"
                  data-testid="super-tenant-name"
                />
              </div>
              <div>
                <Label htmlFor="tenant-slug">Slug (URL handle)</Label>
                <Input
                  id="tenant-slug"
                  required
                  value={slug}
                  onChange={(e) =>
                    setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))
                  }
                  placeholder="sunrise"
                  className="mt-1"
                  data-testid="super-tenant-slug"
                />
                <p className="mt-1 text-xs text-slate-500">
                  Lowercase, numbers and hyphens only.
                </p>
              </div>
              <div>
                <Label htmlFor="tenant-plan">Plan</Label>
                <select
                  id="tenant-plan"
                  value={plan}
                  onChange={(e) => setPlan(e.target.value)}
                  className="mt-1 h-10 w-full rounded-md border border-slate-200 px-3 text-sm"
                  data-testid="super-tenant-plan"
                >
                  <option value="starter">Starter</option>
                  <option value="growth">Growth</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <div className="border-t border-slate-100 pt-4">
                <label className="flex items-center justify-between cursor-pointer">
                  <div>
                    <div className="text-sm font-medium text-[#0A2540]">
                      Create tenant admin account
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Generates a temp password + emails credentials to the admin.
                    </div>
                  </div>
                  <Switch
                    checked={createAdmin}
                    onCheckedChange={setCreateAdmin}
                    data-testid="super-tenant-toggle-admin"
                  />
                </label>
              </div>

              {createAdmin && (
                <div className="space-y-3 rounded-lg border border-[#C9A961]/25 bg-[#FAF9F6] p-4">
                  <div>
                    <Label htmlFor="admin-name" className="text-xs uppercase tracking-wider text-[#0A2540]/70">
                      Admin's name
                    </Label>
                    <Input
                      id="admin-name"
                      value={adminName}
                      onChange={(e) => setAdminName(e.target.value)}
                      placeholder="Amit Sharma"
                      className="mt-1 bg-white"
                      data-testid="super-tenant-admin-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="admin-email" className="text-xs uppercase tracking-wider text-[#0A2540]/70">
                      Admin's email <span className="text-[#C9A961]">*</span>
                    </Label>
                    <Input
                      id="admin-email"
                      type="email"
                      required={createAdmin}
                      value={adminEmail}
                      onChange={(e) => setAdminEmail(e.target.value)}
                      placeholder="amit@sunrisembbs.com"
                      className="mt-1 bg-white"
                      data-testid="super-tenant-admin-email"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs text-[#0A2540]/75 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendWelcome}
                      onChange={(e) => setSendWelcome(e.target.checked)}
                      className="rounded border-slate-300"
                      data-testid="super-tenant-send-welcome"
                    />
                    <Mail className="h-3 w-3" />
                    Email credentials to admin
                  </label>
                </div>
              )}

              <DialogFooter>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
                  data-testid="super-tenant-submit"
                >
                  {submitting ? "Creating…" : "Create tenant"}
                </Button>
              </DialogFooter>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CredField({ label, value, mono, strong, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — value is selectable */
    }
  };
  return (
    <div className="flex items-start gap-3">
      <div className="w-32 shrink-0 text-[10px] uppercase tracking-[0.18em] text-[#0A2540]/55 pt-1.5">
        {label}
      </div>
      <div className="flex-1 flex items-center gap-2">
        <code
          data-testid={testid}
          className={`flex-1 rounded border bg-white px-3 py-2 text-sm break-all select-all ${
            mono ? "font-mono-clock" : ""
          } ${strong ? "font-semibold tracking-wider text-[#0A2540] border-[#C9A961]" : "border-slate-200 text-[#0A2540]"}`}
        >
          {value}
        </code>
        <button
          type="button"
          onClick={copy}
          data-testid={`${testid}-copy`}
          className="shrink-0 inline-flex items-center justify-center h-8 w-8 rounded border border-slate-200 text-slate-500 hover:text-[#0A2540] hover:border-slate-300"
          aria-label={`Copy ${label}`}
        >
          {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}

export default function SuperDashboardPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seedingId, setSeedingId] = useState(null);
  const [usersTenant, setUsersTenant] = useState(null);

  const loadAll = async () => {
    try {
      const [s, t] = await Promise.all([
        api.get("/super/stats"),
        api.get("/super/tenants"),
      ]);
      setStats(s.data);
      setTenants(t.data.tenants || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const onToggleActive = async (tenant) => {
    try {
      await api.patch(`/super/tenants/${tenant.id}`, {
        is_active: !tenant.is_active,
      });
      toast.success(
        `${tenant.name} is now ${!tenant.is_active ? "active" : "paused"}.`,
      );
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onSeedDemo = async (tenant) => {
    if (!window.confirm(
      `Seed 40 demo leads + tasks + campaigns into "${tenant.name}"?\n\nExisting demo data for this tenant will be replaced.`,
    )) return;
    setSeedingId(tenant.id);
    try {
      const r = await api.post(`/super/tenants/${tenant.id}/seed-demo?lead_count=40`);
      const c = r.data.created;
      toast.success(
        `Seeded ${tenant.name}: ${c.leads} leads, ${c.tasks} tasks, ${c.campaigns} campaigns, ${c.activities} activities.`,
        { duration: 6000 },
      );
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSeedingId(null);
    }
  };

  const onClearDemo = async (tenant) => {
    if (!window.confirm(
      `Wipe ALL demo data from "${tenant.name}"?\n\nReal (non-demo) leads stay untouched.`,
    )) return;
    setSeedingId(tenant.id);
    try {
      const r = await api.delete(`/super/tenants/${tenant.id}/seed-demo`);
      const c = r.data.cleared;
      toast.success(
        `Cleared from ${tenant.name}: ${c.leads} leads, ${c.tasks} tasks, ${c.campaigns} campaigns, ${c.activities} activities.`,
      );
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSeedingId(null);
    }
  };

  const onDownloadDeck = async (brand) => {
    try {
      const r = await api.get("/super/sales-deck.pdf", {
        params: brand ? { brand } : {},
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(brand || "MedAdmits").toLowerCase().replace(/\s+/g, "-")}-sales-deck.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Sales deck downloaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onDownloadDeckPrompt = () => {
    const brand = window.prompt(
      "Brand name on the deck cover\n\n(Leave blank for default 'MedAdmits')",
      "MedAdmits",
    );
    if (brand === null) return;  // cancelled
    onDownloadDeck(brand.trim() || "MedAdmits");
  };

  const onLogout = () => {
    logout();
    nav("/admin/login", { replace: true });
  };

  const topTenant = useMemo(() => {
    return [...tenants].sort((a, b) => b.leads_total - a.leads_total)[0];
  }, [tenants]);

  return (
    <div className="min-h-screen bg-slate-50/60">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-10">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-md bg-gradient-to-br from-[#0A2540] to-[#1E3A5F]">
              <Crown className="h-5 w-5 text-[#C9A961]" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <div className="font-display text-base font-bold tracking-tight text-[#0A2540]">
                Super Admin Console
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                MedAdmits SaaS · Cross-Tenant View
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge
              variant="secondary"
              className="hidden bg-[#C9A961]/15 text-[#7A6428] sm:inline-flex"
              data-testid="super-user-badge"
            >
              <ShieldCheck className="mr-1 h-3 w-3" />
              {user?.name || "Super Admin"}
            </Badge>
            <SuperChangePasswordButton />
            <Button
              variant="ghost"
              size="sm"
              onClick={onLogout}
              data-testid="super-logout-btn"
            >
              <LogOut className="mr-1.5 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10">
        {/* Page intro */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A2540]">
              All Consultancies
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Live aggregate of every consultancy on the platform — leads,
              conversions, and team activity.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              className="border-[#C9A961]/40 bg-[#C9A961]/10 text-[#7A6428] hover:bg-[#C9A961]/20"
              onClick={onDownloadDeckPrompt}
              data-testid="super-download-deck"
            >
              <Download className="mr-1.5 h-4 w-4" /> Sales Deck PDF
            </Button>
            <CreateTenantDialog onCreated={loadAll} />
          </div>
        </div>

        {/* Stat cards */}
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <StatCard
            icon={Building2}
            label="Tenants"
            value={stats ? `${stats.active_tenants}/${stats.total_tenants}` : "—"}
            accent="bg-[#0A2540]/10 text-[#0A2540]"
            testid="super-stat-tenants"
          />
          <StatCard
            icon={Users}
            label="Total leads"
            value={stats?.total_leads}
            accent="bg-emerald-100 text-emerald-700"
            testid="super-stat-leads"
          />
          <StatCard
            icon={Flame}
            label="Hot leads"
            value={stats?.total_hot_leads}
            accent="bg-rose-100 text-rose-700"
            testid="super-stat-hot"
          />
          <StatCard
            icon={Activity}
            label="Tests done"
            value={stats?.total_completed_tests}
            accent="bg-amber-100 text-amber-700"
            testid="super-stat-completed"
          />
          <StatCard
            icon={TrendingUp}
            label="Closed deals"
            value={stats?.total_closed}
            accent="bg-violet-100 text-violet-700"
            testid="super-stat-closed"
          />
        </div>

        {/* Top tenant highlight */}
        {topTenant && topTenant.leads_total > 0 && (
          <div
            className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#C9A961]/30 bg-gradient-to-r from-[#C9A961]/10 via-white to-white p-4"
            data-testid="super-top-tenant"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#C9A961]/20">
                <Trophy className="h-5 w-5 text-[#7A6428]" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.16em] text-[#7A6428]">
                  Top consultancy this month
                </div>
                <div className="font-display text-lg font-semibold text-[#0A2540]">
                  {topTenant.name}
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold tabular-nums text-[#0A2540]">
                {topTenant.leads_total}
              </div>
              <div className="text-xs uppercase tracking-[0.14em] text-slate-500">
                Leads captured
              </div>
            </div>
          </div>
        )}

        {/* WhatsApp / MyOperator settings */}
        <WhatsappSettingsPanel />

        {/* Tenants table */}
        <div className="mt-8 rounded-xl border border-slate-200 bg-white">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <h2 className="font-display text-lg font-semibold text-[#0A2540]">
              Tenant directory
            </h2>
            <span className="text-xs text-slate-500">
              {tenants.length} tenant{tenants.length === 1 ? "" : "s"}
            </span>
          </div>
          {loading ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">
              Loading tenants…
            </div>
          ) : tenants.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">
              No tenants yet. Click "New Tenant" to onboard your first
              consultancy.
            </div>
          ) : (
            <Table data-testid="super-tenants-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead className="text-right">Leads</TableHead>
                  <TableHead className="text-right">Hot</TableHead>
                  <TableHead className="text-right">Completed</TableHead>
                  <TableHead className="text-right">Closed</TableHead>
                  <TableHead className="text-right">Staff</TableHead>
                  <TableHead className="text-center">Demo data</TableHead>
                  <TableHead className="text-center">Active</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((t) => (
                  <TableRow
                    key={t.id}
                    data-testid={`super-tenant-row-${t.slug}`}
                    className={!t.is_active ? "opacity-60" : ""}
                  >
                    <TableCell className="font-medium text-[#0A2540]">
                      {t.name}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs">
                        {t.slug}
                      </code>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {t.plan || "starter"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">
                      {t.leads_total}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {t.leads_hot > 0 ? (
                        <span className="inline-flex items-center gap-1 text-rose-600">
                          <Flame className="h-3 w-3" />
                          {t.leads_hot}
                        </span>
                      ) : (
                        <span className="text-slate-400">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {t.leads_completed}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {t.leads_closed}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {t.staff_count}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="inline-flex gap-1">
                        <a
                          href={`/t/${t.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-md border border-[#C9A961]/40 bg-[#C9A961]/10 px-2 py-1 text-[11px] font-medium text-[#7A6428] transition-colors hover:bg-[#C9A961]/20"
                          title="Open branded landing in a new tab"
                          data-testid={`super-preview-${t.slug}`}
                        >
                          <Eye className="h-3 w-3" />
                          Preview
                        </a>
                        <button
                          onClick={() => onSeedDemo(t)}
                          disabled={seedingId === t.id}
                          className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-40"
                          title="Seed 40 realistic leads + tasks + campaigns"
                          data-testid={`super-seed-${t.slug}`}
                        >
                          <Database className="h-3 w-3" />
                          {seedingId === t.id ? "Working…" : "Seed"}
                        </button>
                        <button
                          onClick={() => onClearDemo(t)}
                          disabled={seedingId === t.id}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-600 transition-colors hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40"
                          title="Wipe all demo data (real data stays)"
                          data-testid={`super-clear-${t.slug}`}
                        >
                          <Eraser className="h-3 w-3" />
                          Clear
                        </button>
                        <button
                          onClick={() => setUsersTenant(t)}
                          className="inline-flex items-center gap-1 rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-700 transition-colors hover:bg-indigo-100"
                          title="View tenant users & reset passwords"
                          data-testid={`super-users-${t.slug}`}
                        >
                          <Users className="h-3 w-3" />
                          Users
                        </button>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={!!t.is_active}
                        onCheckedChange={() => onToggleActive(t)}
                        data-testid={`super-tenant-toggle-${t.slug}`}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          Phase A · Multi-tenancy · Super Admin sees aggregate data only —
          tenant data stays isolated.
        </p>

        {/* SaaS sales inquiries — leads from /saas landing */}
        <SaasInquiriesPanel />
      </main>

      {usersTenant && (
        <TenantUsersDialog
          tenant={usersTenant}
          onClose={() => setUsersTenant(null)}
        />
      )}
    </div>
  );
}


function SuperChangePasswordButton() {
  const [open, setOpen] = useState(false);
  const [oldPw, setOldPw] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const rules = {
    length: pw.length >= 12,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
  };
  const allPass = rules.length && rules.upper && rules.lower && rules.digit;
  const matches = pw && pw === pw2;
  const canSubmit = !!oldPw && allPass && matches && !submitting;

  const reset = () => {
    setOldPw("");
    setPw("");
    setPw2("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        old_password: oldPw,
        new_password: pw,
      });
      toast.success("Password updated. Use the new one next time you log in.");
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update password.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          data-testid="super-change-password-btn"
        >
          <KeyRound className="mr-1.5 h-4 w-4" />
          Change password
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="super-change-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0A2540]">Change password</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 pt-2">
          <div>
            <Label htmlFor="scpw-old" className="text-sm">Current password</Label>
            <Input
              id="scpw-old"
              type="password"
              required
              value={oldPw}
              onChange={(e) => setOldPw(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5 h-11"
              data-testid="scpw-old"
            />
          </div>
          <div>
            <Label htmlFor="scpw-new" className="text-sm">New password</Label>
            <Input
              id="scpw-new"
              type="password"
              required
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="At least 12 characters"
              className="mt-1.5 h-11"
              data-testid="scpw-new"
            />
          </div>
          <div>
            <Label htmlFor="scpw-confirm" className="text-sm">Confirm new password</Label>
            <Input
              id="scpw-confirm"
              type="password"
              required
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              placeholder="••••••••"
              className="mt-1.5 h-11"
              data-testid="scpw-confirm"
            />
          </div>
          <ul className="text-xs space-y-1">
            <SuperRule ok={rules.length} text="At least 12 characters" />
            <SuperRule ok={rules.upper} text="One uppercase letter" />
            <SuperRule ok={rules.lower} text="One lowercase letter" />
            <SuperRule ok={rules.digit} text="One digit" />
            <SuperRule ok={!!matches} text="Passwords match" />
          </ul>
          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              data-testid="scpw-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="bg-[#0A2540] hover:bg-[#1E3A5F] disabled:opacity-50"
              data-testid="scpw-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Updating…
                </>
              ) : (
                "Update password"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SuperRule({ ok, text }) {
  return (
    <li
      className={`flex items-center gap-1.5 ${
        ok ? "text-emerald-700" : "text-slate-500"
      }`}
    >
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full ${
          ok ? "bg-emerald-500" : "bg-slate-300"
        }`}
      />
      {text}
    </li>
  );
}



// ---------- Tenant Users Dialog ----------

function TenantUsersDialog({ tenant, onClose }) {
  const [users, setUsers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [tempCred, setTempCred] = useState(null); // {email, name, new_password}

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/super/tenants/${tenant.id}/users`);
      setUsers(r.data.users || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant.id]);

  const onResetTemp = async (u) => {
    if (
      !window.confirm(
        `Generate a temporary password for ${u.email}?\n\nThe password will be shown to you ONCE. Share it with the user via WhatsApp / phone. They'll be forced to change it on next login.`,
      )
    )
      return;
    setActingId(u.id);
    try {
      const r = await api.post(`/super/users/${u.id}/reset-password`, {});
      setTempCred({
        email: r.data.email,
        name: r.data.name,
        new_password: r.data.new_password,
      });
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActingId(null);
    }
  };

  const onSendResetLink = async (u) => {
    if (
      !window.confirm(
        `Send a password reset email to ${u.email}?\n\nThe link is valid for 30 minutes.`,
      )
    )
      return;
    setActingId(u.id);
    try {
      await api.post(`/super/users/${u.id}/send-reset-link`);
      toast.success(`Reset link emailed to ${u.email}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActingId(null);
    }
  };

  const [editing, setEditing] = useState(null); // user being edited
  const onSaveEdit = async (userId, newEmail, newName) => {
    setActingId(userId);
    try {
      await api.patch(`/super/users/${userId}`, {
        email: newEmail,
        name: newName,
      });
      toast.success("User updated.");
      setEditing(null);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setActingId(null);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl"
        data-testid="super-tenant-users-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#0A2540] flex items-center gap-2">
            <Users className="h-5 w-5" />
            {tenant.name} · users
          </DialogTitle>
        </DialogHeader>

        {tempCred && (
          <TempPasswordReveal
            cred={tempCred}
            onDismiss={() => setTempCred(null)}
          />
        )}

        <div className="max-h-[60vh] overflow-auto rounded-md border border-slate-200">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <Loader2 className="inline mr-2 h-4 w-4 animate-spin" />
              Loading users…
            </div>
          ) : !users || users.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">
              No users found for this tenant.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">Last pwd change</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr
                    key={u.id}
                    className="border-t border-slate-100"
                    data-testid={`tenant-user-row-${u.email}`}
                  >
                    <td className="px-3 py-3">
                      <div className="font-medium text-[#0A2540]">
                        {u.name || "—"}
                      </div>
                      <div className="text-xs text-slate-500">{u.email}</div>
                      {u.must_change_password && (
                        <Badge
                          variant="outline"
                          className="mt-1 border-amber-300 bg-amber-50 text-[10px] text-amber-700"
                        >
                          Must change password
                        </Badge>
                      )}
                      {u.is_active === false && (
                        <Badge
                          variant="outline"
                          className="mt-1 border-slate-300 bg-slate-100 text-[10px] text-slate-500"
                        >
                          Deactivated
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-3">
                      <Badge
                        variant="secondary"
                        className={
                          u.role === "admin"
                            ? "bg-[#C9A961]/15 text-[#7A6428]"
                            : "bg-slate-100 text-slate-700"
                        }
                      >
                        {u.role}
                      </Badge>
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-500">
                      {u.password_changed_at
                        ? new Date(u.password_changed_at).toLocaleDateString()
                        : "never"}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="inline-flex gap-1.5">
                        <button
                          onClick={() => setEditing(u)}
                          disabled={actingId === u.id}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-40"
                          title="Edit name or email"
                          data-testid={`edit-user-${u.email}`}
                        >
                          <Pencil className="h-3 w-3" />
                          Edit
                        </button>
                        <button
                          onClick={() => onSendResetLink(u)}
                          disabled={actingId === u.id}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-40"
                          title="Email a 30-min reset link"
                          data-testid={`send-reset-link-${u.email}`}
                        >
                          <Mail className="h-3 w-3" />
                          Email link
                        </button>
                        <button
                          onClick={() => onResetTemp(u)}
                          disabled={actingId === u.id}
                          className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-medium text-rose-700 transition-colors hover:bg-rose-100 disabled:opacity-40"
                          title="Generate a temp password (shown once)"
                          data-testid={`reset-temp-pwd-${u.email}`}
                        >
                          <KeyRound className="h-3 w-3" />
                          {actingId === u.id ? "Working…" : "Temp pwd"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {editing && (
          <EditUserDialog
            user={editing}
            onClose={() => setEditing(null)}
            onSave={onSaveEdit}
            saving={actingId === editing.id}
          />
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="super-users-dialog-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Edit User (inline mini-dialog) ----------
function EditUserDialog({ user, onClose, onSave, saving }) {
  const [email, setEmail] = useState(user.email || "");
  const [name, setName] = useState(user.name || "");
  const dirty = email !== (user.email || "") || name !== (user.name || "");

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-md"
        data-testid="super-edit-user-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#0A2540] flex items-center gap-2">
            <Pencil className="h-4 w-4" /> Edit user
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Display name
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1"
              data-testid="edit-user-name"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">
              Email
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 font-mono text-sm"
              data-testid="edit-user-email"
            />
            <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
              Changing the email also lets the user log in with the new
              address. The old address goes inactive immediately. If the old
              address was on Resend&apos;s suppression list, the user starts
              getting emails again from the very next send.
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={() => onSave(user.id, email, name)}
            disabled={!dirty || saving || !email.trim() || !name.trim()}
            className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
            data-testid="edit-user-save"
          >
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function TempPasswordReveal({ cred, onDismiss }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(cred.new_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Copy failed — select the value manually.");
    }
  };
  return (
    <div
      className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm"
      data-testid="temp-password-reveal"
    >
      <div className="font-semibold text-amber-900">
        Temporary password generated for {cred.name || cred.email}
      </div>
      <div className="mt-1 text-xs text-amber-800">
        This is the only time you'll see this password. Share it with the user
        via WhatsApp / phone. They'll be forced to change it on next login.
      </div>
      <div className="mt-3 flex items-center gap-2">
        <code
          className="flex-1 rounded bg-white px-3 py-2 font-mono-clock text-sm font-semibold tracking-wider text-[#0A2540] border border-amber-200 select-all"
          data-testid="temp-password-value"
        >
          {cred.new_password}
        </code>
        <Button
          size="sm"
          onClick={onCopy}
          className="bg-[#0A2540] hover:bg-[#1E3A5F]"
          data-testid="temp-password-copy"
        >
          {copied ? (
            <>
              <Check className="mr-1 h-3.5 w-3.5" /> Copied
            </>
          ) : (
            <>
              <Copy className="mr-1 h-3.5 w-3.5" /> Copy
            </>
          )}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={onDismiss}
          data-testid="temp-password-dismiss"
        >
          Dismiss
        </Button>
      </div>
    </div>
  );
}


function WhatsappSettingsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [justSavedKey, setJustSavedKey] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [form, setForm] = useState({
    enabled: false,
    base_url: "",
    api_key: "",
    company_id: "",
    waba_id: "",
    phone_number_id: "",
    send_endpoint: "",
  });

  const load = async () => {
    try {
      const r = await api.get("/admin/whatsapp/settings");
      setData(r.data);
      setForm({
        enabled: r.data.enabled,
        base_url: r.data.base_url || "",
        api_key: "",
        company_id: r.data.company_id || "",
        waba_id: r.data.waba_id || "",
        phone_number_id: r.data.phone_number_id || "",
        send_endpoint: r.data.send_endpoint || "",
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    const hadNewKey = !!form.api_key;
    const newKeyTail = hadNewKey ? form.api_key.trim().slice(-4) : "";
    try {
      const body = { ...form };
      if (!body.api_key) delete body.api_key; // don't clobber existing key with empty
      await api.put("/admin/whatsapp/settings", body);
      if (hadNewKey) {
        toast.success(`API key updated · new token ends in …${newKeyTail}`, {
          duration: 5000,
        });
        setJustSavedKey(true);
        setTimeout(() => setJustSavedKey(false), 6000);
      } else {
        toast.success("WhatsApp settings saved");
      }
      setForm({ ...form, api_key: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post("/admin/whatsapp/test-connection");
      setTestResult(r.data);
    } catch (e) {
      setTestResult({
        ok: false,
        stage: "client",
        diagnosis: "CLIENT_ERROR",
        message: formatApiError(e),
        http_status: e?.response?.status ?? null,
        myoperator_response: e?.response?.data ?? null,
      });
    } finally {
      setTesting(false);
    }
  };


  if (loading) {
    return (
      <div
        className="mt-8 rounded-xl border border-slate-200 bg-white p-5"
        data-testid="wa-settings-panel"
      >
        <div className="text-sm text-slate-500">Loading WhatsApp settings…</div>
      </div>
    );
  }

  return (
    <div
      className="mt-8 rounded-xl border border-slate-200 bg-white p-5"
      data-testid="wa-settings-panel"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-semibold text-[#0A2540]">
            WhatsApp · MyOperator integration
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Securely store outbound API credentials. The API key is masked on
            read &amp; only overwritten when you supply a new value.
          </p>
        </div>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            data?.enabled && data?.api_key_set
              ? "bg-emerald-100 text-emerald-700"
              : data?.enabled && !data?.api_key_set
              ? "bg-rose-100 text-rose-700"
              : "bg-slate-100 text-slate-600"
          }`}
        >
          {data?.enabled && data?.api_key_set
            ? "✓ Active"
            : data?.enabled && !data?.api_key_set
            ? "⚠ Missing API key"
            : "Inactive"}
        </span>
      </div>

      {data?.enabled && !data?.api_key_set && (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50/60 p-3 text-xs text-rose-800">
          <strong>API key not yet saved.</strong> Outbound sending will fail
          with &ldquo;Unauthorised&rdquo;. Paste your regenerated MyOperator
          token below &amp; click <em>Save settings</em>.
        </div>
      )}

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <Label className="text-xs text-slate-500">Base URL</Label>
          <Input
            value={form.base_url}
            onChange={(e) => setForm({ ...form, base_url: e.target.value })}
            className="mt-1"
            data-testid="wa-base-url"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">
            API key {data?.api_key_set && "(set)"}
          </Label>
          <Input
            type="password"
            value={form.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            placeholder={
              data?.api_key_set
                ? "Leave blank to keep current key"
                : "Paste new auth token (regenerated from MyOperator)"
            }
            className="mt-1 font-mono text-xs"
            data-testid="wa-api-key"
          />
          {data?.api_key_set && (
            <div
              className={`mt-2 flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[11px] transition-colors ${
                justSavedKey
                  ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                  : "border-slate-200 bg-slate-50 text-slate-600"
              }`}
              data-testid="wa-api-key-fingerprint"
            >
              <Check className="h-3 w-3 shrink-0" />
              <span className="font-medium">Saved key:</span>
              <span className="font-mono">{data.api_key_masked}</span>
              {justSavedKey && (
                <span className="ml-auto rounded-full bg-emerald-200/70 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                  Just updated
                </span>
              )}
            </div>
          )}
        </div>
        <div>
          <Label className="text-xs text-slate-500">Company ID</Label>
          <Input
            value={form.company_id}
            onChange={(e) => setForm({ ...form, company_id: e.target.value })}
            className="mt-1 font-mono text-xs"
            data-testid="wa-company-id"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">WABA ID</Label>
          <Input
            value={form.waba_id}
            onChange={(e) => setForm({ ...form, waba_id: e.target.value })}
            className="mt-1 font-mono text-xs"
            data-testid="wa-waba-id"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">
            Phone number ID (your WABA number)
          </Label>
          <Input
            value={form.phone_number_id}
            onChange={(e) =>
              setForm({ ...form, phone_number_id: e.target.value })
            }
            className="mt-1 font-mono text-xs"
            data-testid="wa-phone-number-id"
          />
        </div>
        <div>
          <Label className="text-xs text-slate-500">
            Send endpoint (advanced)
          </Label>
          <Input
            value={form.send_endpoint}
            onChange={(e) =>
              setForm({ ...form, send_endpoint: e.target.value })
            }
            placeholder="/chat/messages"
            className="mt-1 font-mono text-xs"
            data-testid="wa-send-endpoint"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            className="h-4 w-4"
            data-testid="wa-enabled"
          />
          Enable outbound WhatsApp sending
        </label>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            onClick={runTest}
            disabled={testing || !data?.api_key_set}
            variant="outline"
            className="border-slate-300 text-slate-700 hover:bg-slate-50"
            data-testid="wa-test-connection"
            title={
              !data?.api_key_set
                ? "Save an API key first"
                : "Probe MyOperator with the saved credentials"
            }
          >
            {testing ? "Testing…" : "Test connection"}
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#0A2540] text-white hover:bg-[#1E3A5F]"
            data-testid="wa-save-settings"
          >
            {saving ? "Saving…" : "Save settings"}
          </Button>
        </div>
      </div>

      {testResult && (
        <div
          className={`mt-4 rounded-lg border p-4 text-sm ${
            testResult.ok
              ? "border-emerald-300 bg-emerald-50 text-emerald-900"
              : "border-rose-300 bg-rose-50 text-rose-900"
          }`}
          data-testid="wa-test-result"
        >
          <div className="flex items-center gap-2">
            <span className="text-base font-semibold">
              {testResult.ok ? "✓" : "✕"}
            </span>
            <span className="font-semibold uppercase tracking-wide text-xs">
              {testResult.diagnosis}
            </span>
            {testResult.http_status != null && (
              <span className="ml-1 rounded-md bg-white/60 px-1.5 py-0.5 font-mono text-[11px]">
                HTTP {testResult.http_status}
              </span>
            )}
            <button
              type="button"
              onClick={() => setTestResult(null)}
              className="ml-auto text-xs underline opacity-70 hover:opacity-100"
              data-testid="wa-test-result-dismiss"
            >
              Dismiss
            </button>
          </div>
          <p className="mt-2 whitespace-pre-line leading-relaxed">{testResult.message}</p>
          {testResult.key_fingerprint && (
            <p className="mt-2 font-mono text-[11px] opacity-70">
              Key fingerprint: {testResult.key_fingerprint}
            </p>
          )}
          {testResult.myoperator_response && (
            <details className="mt-2">
              <summary className="cursor-pointer text-xs font-medium opacity-80 hover:opacity-100">
                Raw MyOperator response
              </summary>
              <pre className="mt-1.5 max-h-48 overflow-auto rounded-md bg-white/70 p-2 font-mono text-[11px] leading-snug text-slate-800">
                {JSON.stringify(testResult.myoperator_response, null, 2)}
              </pre>
            </details>
          )}
          {testResult.probe_endpoint && (
            <p className="mt-2 font-mono text-[10px] opacity-60">
              Probed: {testResult.probe_endpoint}
            </p>
          )}
        </div>
      )}

      {data?.updated_at && (
        <p className="mt-3 text-[11px] text-slate-500">
          Last updated by {data.updated_by} ·{" "}
          {new Date(data.updated_at).toLocaleString()}
        </p>
      )}
    </div>
  );
}

